import { Card } from '@shared/ui';

import { isActive, isUnresponsive, jobProgress } from '../model/job-progress';
import { MODE_LABEL } from '../model/navigation';

import { ProgressBar } from './table';

import type { JobHeader } from '../schemas/bulk-migration';

/**
 * Where a job is: its progress while it runs, who asked and why, and — when it
 * stopped moving for ten minutes — that it did. A tenant whose message died
 * twice never reports back; this is what keeps that from looking like work.
 */
export function JobProgressCard({ job, now }: { job: JobHeader; now: number }) {
    const progress = jobProgress(job);
    const stuck = isUnresponsive(job, now);

    return (
        <Card className="mb-4">
            <div className="flex flex-col gap-3 px-5 py-4">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="text-[15px] font-semibold text-content">
                        {MODE_LABEL[job.mode]}
                    </span>
                    <span className="font-mono text-[11.5px] text-content-faint">
                        {job.reportId}
                    </span>
                    <span className="text-[12.5px] text-content-muted">
                        by {job.requestedBy} · {new Date(job.createdAt).toLocaleString()}
                    </span>
                </div>
                {job.reason && (
                    <p className="text-[13px] text-content-body">
                        <span className="text-content-muted">Reason: </span>
                        {job.reason}
                    </p>
                )}
                {isActive(job.state) && (
                    <div className="max-w-md">
                        <ProgressBar label={progress.label} percent={progress.percent} />
                    </div>
                )}
                {stuck && (
                    <p
                        role="alert"
                        className="rounded-lg border border-warning-line bg-warning-surface px-3.5 py-2.5 text-[12.5px] text-warning-text"
                    >
                        No response for more than 10 minutes. Some clients may never report back —
                        their messages are in the runner&apos;s dead-letter queue. Start a new
                        analysis for those clients to retry them.
                    </p>
                )}
                {job.state === 'failed' && (
                    <p
                        role="alert"
                        className="rounded-lg border border-danger-line bg-danger-surface px-3.5 py-2.5 text-[12.5px] text-danger-text"
                    >
                        This job failed before it could list the clients
                        {job.error ? `: ${job.error}` : '.'}
                    </p>
                )}
            </div>
        </Card>
    );
}
